import { CommonModule } from '@angular/common';
import { Component, ElementRef, EventEmitter, HostListener, Input, OnChanges, Output, SimpleChanges, ViewChild, inject } from '@angular/core';
import { FormsModule } from '@angular/forms';

import type { TemplateTextboxResizeType, TemplateTextboxValidationType } from '../../models/template.models';
import type {
  FieldConfiguratorActor,
  FieldConfiguratorField,
  FieldConfiguratorOptionSets,
  FieldConfiguratorSettingAction,
  FieldConfiguratorSettingsPanelOptions,
} from './field-configurator.models';
import { fieldSupportsCharacterSpacing } from './field-configurator.utils';

type FontStyleKey = 'bold' | 'italic' | 'underline';

@Component({
  selector: 'app-field-settings-panel',
  imports: [CommonModule, FormsModule],
  templateUrl: './field-settings-panel.component.html',
  styleUrl: './field-settings-panel.component.scss',
})
export class FieldSettingsPanelComponent {
  private readonly elementRef = inject(ElementRef<HTMLElement>);
  private static readonly TEXTBOX_RESIZE_META: Record<
    TemplateTextboxResizeType,
    { label: string; description: string }
  > = {
    grow_horizontally: {
      label: 'Grow Horizontally',
      description: 'Expands width to fit text.',
    },
    grow_vertically: {
      label: 'Grow Vertically',
      description: 'Expands height to fit text.',
    },
    grow_both: {
      label: 'Grow Both Horizontally and Vertically',
      description: 'Expands width and height to fit text.',
    },
    fixed: {
      label: 'Fixed Size',
      description: 'Text must fit within the box.',
    },
    auto_resize_font: {
      label: 'Auto Resize Font',
      description: 'Adjusts font size to fit text.',
    },
  };
  private static readonly TEXTBOX_VALIDATION_META: Record<Exclude<TemplateTextboxValidationType, 'regex'>, string> = {
    none: 'None',
    email: 'Email',
    number: 'Only Numbers',
    minimum: 'Minimum',
    maximum: 'Maximum',
  };
  private static readonly TEXTBOX_VALIDATION_UI_OPTIONS = ['none', 'email', 'number', 'minimum', 'maximum'] as const;

  private static readonly FONT_COLOR_PRESETS = [
    '#F31225',
    '#7A0F2E',
    '#A95D2A',
    '#F89A2A',
    '#0D8E4D',
    '#5FA03A',
    '#5C0E5C',
    '#EC8EB9',
    '#123A86',
    '#38424E',
    '#0E4B90',
    '#5A9ACF',
    '#18AEDD',
    '#8E8E8E',
    '#DCDCDC',
  ] as const;

  @Input() title = 'Field settings';
  @Input() selectedField: FieldConfiguratorField | null = null;
  @Input() actors: FieldConfiguratorActor[] = [];
  @Input() panelOptions!: FieldConfiguratorSettingsPanelOptions;
  @Input() optionSets!: FieldConfiguratorOptionSets;
  @Input() positionX = 0;
  @Input() positionY = 0;
  @Input() checkboxGroupName = '';
  @Input() checkboxGroupValues: string[] = [];
  @Input() checkboxGroupDefaultValues: string[] = [];
  @Input() radioGroupName = '';
  @Input() radioGroupValues: string[] = [];
  @Input() radioGroupDefaultValue: string | undefined;
  @Output() action = new EventEmitter<FieldConfiguratorSettingAction>();
  @ViewChild('fontColorTrigger') private readonly fontColorTrigger?: ElementRef<HTMLButtonElement>;

  readonly fontColorPresets = FieldSettingsPanelComponent.FONT_COLOR_PRESETS;
  isFontColorPopoverOpen = false;
  draftFontColor = '#000000';

  emit(action: FieldConfiguratorSettingAction): void {
    this.action.emit(action);
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['selectedField']) {
      if (this.isFontColorPopoverOpen) {
        this.isFontColorPopoverOpen = false;
        queueMicrotask(() => this.resetDraftFontColor());
        return;
      }

      this.resetDraftFontColor();
    }
  }

  showCharacterSpacingControl(): boolean {
    return !!this.selectedField && fieldSupportsCharacterSpacing(this.selectedField.type);
  }

  textboxResizeLabel(resizeType: TemplateTextboxResizeType): string {
    return FieldSettingsPanelComponent.TEXTBOX_RESIZE_META[resizeType].label;
  }

  textboxResizeDescription(resizeType: TemplateTextboxResizeType): string {
    return FieldSettingsPanelComponent.TEXTBOX_RESIZE_META[resizeType].description;
  }

  textboxValidationOptions(): readonly Exclude<TemplateTextboxValidationType, 'regex'>[] {
    return FieldSettingsPanelComponent.TEXTBOX_VALIDATION_UI_OPTIONS;
  }

  textboxValidationLabel(validationType: Exclude<TemplateTextboxValidationType, 'regex'>): string {
    return FieldSettingsPanelComponent.TEXTBOX_VALIDATION_META[validationType];
  }

  textboxValidationSelection(): Exclude<TemplateTextboxValidationType, 'regex'> {
    const validationType = this.selectedField?.settings.textbox.validationType;
    if (validationType === 'email' || validationType === 'number' || validationType === 'minimum' || validationType === 'maximum') {
      return validationType;
    }

    return 'none';
  }

  selectedTextboxResizeType(): TemplateTextboxResizeType {
    return this.selectedField?.settings.textbox.resizeType ?? 'grow_vertically';
  }

  shouldShowTextboxValidationValue(): boolean {
    const validationType = this.selectedField?.settings.textbox.validationType;
    return validationType === 'minimum' || validationType === 'maximum';
  }

  toggleFontColorPopover(): void {
    if (!this.selectedField) {
      return;
    }

    if (this.isFontColorPopoverOpen) {
      this.closeFontColorPopover();
      return;
    }

    this.resetDraftFontColor();
    this.isFontColorPopoverOpen = true;
  }

  onFontColorDraftChange(value: string): void {
    this.draftFontColor = value;
  }

  applyPresetFontColor(color: string): void {
    this.draftFontColor = color.toLowerCase();
  }

  applyFontColor(): void {
    if (!this.canApplyFontColor()) {
      return;
    }

    this.emit({
      type: 'update-font',
      payload: { color: this.normalizedDraftFontColor() },
    });
    this.closeFontColorPopover();
  }

  cancelFontColorPopover(): void {
    this.closeFontColorPopover();
  }

  canApplyFontColor(): boolean {
    return this.isValidHexColor(this.draftFontColor);
  }

  normalizedDraftFontColor(): string {
    return this.canApplyFontColor() ? this.draftFontColor.toLowerCase() : this.currentFontColor();
  }

  @HostListener('document:click', ['$event'])
  onDocumentClick(event: MouseEvent): void {
    if (!this.isFontColorPopoverOpen) {
      return;
    }

    const target = event.target;
    if (!(target instanceof Node)) {
      return;
    }

    if (this.elementRef.nativeElement.contains(target)) {
      return;
    }

    this.closeFontColorPopover();
  }

  @HostListener('document:keydown.escape')
  onEscapePressed(): void {
    if (!this.isFontColorPopoverOpen) {
      return;
    }

    this.closeFontColorPopover();
  }

  toggleExclusiveFontStyle(style: FontStyleKey): void {
    if (!this.selectedField) {
      return;
    }

    const isActive = this.selectedField.settings.font[style];

    this.emit({
      type: 'update-font',
      payload: {
        bold: style === 'bold' ? !isActive : false,
        italic: style === 'italic' ? !isActive : false,
        underline: style === 'underline' ? !isActive : false,
      },
    });
  }

  private currentFontColor(): string {
    const color = this.selectedField?.settings.font.color ?? '#000000';
    return this.isValidHexColor(color) ? color.toLowerCase() : '#000000';
  }

  private resetDraftFontColor(): void {
    this.draftFontColor = this.currentFontColor();
  }

  private closeFontColorPopover(restoreFocus = true): void {
    this.isFontColorPopoverOpen = false;
    this.resetDraftFontColor();
    if (restoreFocus) {
      setTimeout(() => this.fontColorTrigger?.nativeElement.focus(), 0);
    }
  }

  private isValidHexColor(value: string | undefined): boolean {
    return typeof value === 'string' && /^#[0-9a-fA-F]{6}$/.test(value.trim());
  }
}
